<template>
  <div>
    <app-cells>
      <app-button @click.native="onShowModal(null)">Create</app-button>
      <app-select
        @input="fetchItems"
        v-model="advertising_source"
        :options="advertising_source_options"
        :reduce="item => item.code"
        placeholder="Advertising source"
        class="select--200"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #source="{ row }">{{ row.advertising_source.name }}</template>
        <template #month="{ row }">{{ dateMonthFormat(row.month) }}</template>
        <template #amount="{ row }">{{ new Intl.NumberFormat('ru-RU').format(row.amount) }}</template>
        <template #created_at="{ row }">{{ dateTimeFormat(row.month) }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
            <button @click="onShowDeleteModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="expenses-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal" class="popup__close" type="button"></button>
        <div class="popup__title">{{ expense_to_action ? 'Edit' : 'Create' }}</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Source" required>
              <app-select
                v-model="form.advertising_source"
                :options="advertising_source_options"
                :clearable="false"
                placeholder="Advertising source"
                :error="$v.form.advertising_source.$error"
              />
              <template #error>
                <div v-if="$v.form.advertising_source.$dirty && !$v.form.advertising_source.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Month" required>
              <app-date-picker
                v-model="form.month"
                format="MMM YYYY"
                placeholder="Month"
                type="month"
                :error="$v.form.month.$error"
              />
              <template #error>
                <div v-if="$v.form.month.$dirty && !$v.form.month.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Value" label-for="amount" required>
              <app-fin-number
                v-model="form.amount"
                id="amount"
                name="amount"
                :error="$v.form.amount.$error"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">
                {{ expense_to_action ? 'Save' : 'Create' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-expenses-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-expenses-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this item?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-expenses-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteLeadsAdvertisingExpenses,
  getLeadsAdvertisingExpenses,
  getLeadsAdvSources,
  patchLeadsAdvertisingExpenses,
  postLeadsAdvertisingExpenses,
} from '@/http'
import { required } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'

export default {
  name: 'CRMAnalyticsExpenses',
  data() {
    return {
      page_loaded: false,
      table: {
        items: [],
        columns: [
          'source',
          'month',
          'amount',
          'created_at',
          'controls',
        ],
        options: {
          headings: {
            'source': 'Source',
            'month': 'Month',
            'amount': 'Value',
            'created_at': 'Created at',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      advertising_source: null,
      advertising_source_options: [],
      expense_to_action: null,
      form: {},
    }
  },
  validations: {
    form: {
      advertising_source: { required },
      month: { required },
      amount: { required },
    },
  },
  created() {
    this.fetchItems()
    getLeadsAdvSources().then(response => { this.advertising_source_options = response.data })
  },
  methods: {
    fetchItems() {
      getLeadsAdvertisingExpenses({
        page: this.pagination.page,
        page_size: 20,
        advertising_source: this.advertising_source
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.page_loaded = true
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.expense_to_action) {
        patchLeadsAdvertisingExpenses(this.expense_to_action, {...this.form, advertising_source: this.form.advertising_source.code })
          .then(() => {
            this.fetchItems()
            this.$refs.submit.preload = false
            this.onHideModal()
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        postLeadsAdvertisingExpenses({...this.form, advertising_source: this.form.advertising_source.code })
          .then(() => {
            this.fetchItems()
            this.$refs.submit.preload = false
            this.onHideModal()
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onDeleteItem() {
      this.$refs.delete.preload = true
      deleteLeadsAdvertisingExpenses(this.expense_to_action)
        .then(() => {
          this.fetchItems()
          this.$modal.hide('delete-expenses-modal')
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onShowModal(row) {
      this.expense_to_action = row?.id
      if (this.expense_to_action) {
        this.form = {
          amount: row.amount,
          advertising_source: row.advertising_source,
          month: row.month,
        }
      } else {
        this.form = {}
      }
      this.$v.form.$reset()
      this.$modal.show('expenses-modal')
    },
    onHideModal() {
      this.$modal.hide('expenses-modal')
    },
    onShowDeleteModal(id) {
      this.expense_to_action = id
      this.$modal.show('delete-expenses-modal')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateMonthFormat(date) {
      return date ? format(parseISO(date), 'MMM yyyy') : '-'
    },
  }
}
</script>
